import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, ArrowLeft, Check, Mail, AlertCircle, Loader2,
  Building2, User, Users, MailPlus, ShieldCheck, Plus, Info,
  Search, ChevronDown, AlertTriangle,
} from "lucide-react";
import SEO, { breadcrumb } from "../components/SEO";
import { useProAuth } from "../components/ProAuthContext";
import { UserIdField } from "../components/UserIdField";
import StructureAnnuaireForm from "../components/mss/StructureAnnuaireForm";
import ProsAnnuaireSuggestions, { professionConnue, prenomPropre } from "../components/mss/ProsAnnuaireSuggestions";
import { getIdTypeMeta, validateUserId, getUserBadge } from "../lib/userIdTypes";
import VerifyEmailBanner from "../components/VerifyEmailBanner";
import { API } from "../apiBase";
import { PROFESSIONS } from "../lib/professions";
const STORAGE_KEY = "mc_mssante_create_v3";

const MSSANTE_DOMAINS = [
  "optique.mssante.fr",
  "opticiens.mssante.fr",
  "ohmyvue.mssante.fr",
];

const validMailboxName = (s) => /^[a-z0-9-]+$/.test(s || "") && s.length >= 3;

const SCHEMA = [breadcrumb([
  { name: "Accueil", url: "/" },
  { name: "MSSanté", url: "/mssante" },
  { name: "Espace sécurisé", url: "/mssante/espace" },
  { name: "Nouvelle messagerie", url: "/mssante/espace/messagerie/nouvelle" },
])];

export default function MssanteCreerMessagerie() {
  const { user, refresh } = useProAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [structures, setStructures] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingStructures, setLoadingStructures] = useState(true);

  const [form, setForm] = useState({
    structure_ref_id: "",
    responsible_user_id: "",
    cotitulaire_user_ids: [],
    delegataire_user_ids: [],
    mailbox_name: "",
    mailbox_domain: MSSANTE_DOMAINS[0],
    mailbox_description: "Boîte Mail organisationnelle",
    mailbox_red_list: false,
    billing_ref: "",
    billing_plan: "monthly",
    contract_accepted: false,
    website: "",
  });

  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [doneEmail, setDoneEmail] = useState("");

  // Load my structures
  useEffect(() => {
    (async () => {
      setLoadingStructures(true);
      try {
        const { data } = await axios.get(`${API}/pro/structures`);
        setStructures(data || []);
        // restore from local
        try {
          const raw = localStorage.getItem(STORAGE_KEY);
          if (raw) {
            const saved = JSON.parse(raw);
            if (saved.form) setForm((f) => ({ ...f, ...saved.form }));
            if (saved.step) setStep(saved.step);
          }
        } catch (e) { console.warn("create: localStorage restore skipped", e); }
      } finally {
        setLoadingStructures(false);
      }
    })();
  }, []);

  // Load users when structure changes
  useEffect(() => {
    if (!form.structure_ref_id) { setUsers([]); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/pro/structures/${form.structure_ref_id}/users`);
        setUsers(data || []);
      } catch (e) {
        console.warn("create: load users failed", e);
        setUsers([]);
      }
    })();
  }, [form.structure_ref_id]);

  // When structure or responsable changes, clean up co/del lists
  useEffect(() => {
    setForm((f) => {
      const ids = new Set((users || []).map((u) => u.id));
      const co = (f.cotitulaire_user_ids || []).filter((x) => ids.has(x) && x !== f.responsible_user_id);
      const dl = (f.delegataire_user_ids || []).filter((x) => ids.has(x) && x !== f.responsible_user_id && !co.includes(x));
      if (co.length === f.cotitulaire_user_ids.length && dl.length === f.delegataire_user_ids.length) return f;
      return { ...f, cotitulaire_user_ids: co, delegataire_user_ids: dl };
    });
  }, [users, form.responsible_user_id]);

  useEffect(() => {
    if (done) return;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ form, step })); }
    catch (e) { console.warn("create: localStorage save skipped", e); }
  }, [form, step, done]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const fullEmail = form.mailbox_name ? `${form.mailbox_name}@${form.mailbox_domain}` : "";
  const selectedStructure = structures.find((s) => s.id === form.structure_ref_id);
  const selectedUser = users.find((u) => u.id === form.responsible_user_id);

  const validateStep = (n) => {
    const e = {};
    if (n === 1) {
      if (!form.structure_ref_id) e.structure_ref_id = "Sélectionnez une structure";
    }
    if (n === 2) {
      if (!form.responsible_user_id) e.responsible_user_id = "Sélectionnez un responsable";
    }
    if (n === 3) {
      if (!validMailboxName(form.mailbox_name))
        e.mailbox_name = "Minuscules, chiffres et tirets uniquement (3 car. min.)";
      if (form.mailbox_description.trim().length < 2)
        e.mailbox_description = "Ce champ est requis";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validateStep(step)) setStep(step + 1); };
  const prev = () => setStep(step - 1);

  const submit = async () => {
    setServerError("");
    if (!validateStep(3)) return;
    if (!form.contract_accepted) {
      setServerError("Vous devez accepter le contrat MSSanté pour finaliser la souscription.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/mssante-orders`, {
        structure_ref_id: form.structure_ref_id,
        responsible_user_id: form.responsible_user_id,
        cotitulaire_user_ids: form.cotitulaire_user_ids,
        delegataire_user_ids: form.delegataire_user_ids,
        mailbox_name: form.mailbox_name,
        mailbox_domain: form.mailbox_domain,
        mailbox_description: form.mailbox_description,
        mailbox_red_list: form.mailbox_red_list,
        billing_ref: form.billing_ref || null,
        billing_plan: form.billing_plan,
        contract_accepted: true,
        contract_version: "v1",
        website: form.website,
      });
      // Order created — open Stripe Checkout (subscription mode, SEPA)
      try {
        const orderId = data?.id;
        if (orderId) {
          const { data: sess } = await axios.post(`${API}/pro/mssante-orders/${orderId}/checkout`);
          try { localStorage.removeItem(STORAGE_KEY); } catch (e) { console.warn(e); }
          if (sess?.url) {
            window.location.href = sess.url;
            return;
          }
        }
      } catch (paymentErr) {
        console.error("Stripe checkout failed:", paymentErr);
        setServerError("La BAL a été créée mais la session de paiement n'a pas pu être ouverte. Retrouvez-la dans votre espace pour relancer la souscription.");
      }
      setDoneEmail(data?.mailbox_full_email || fullEmail);
      setDone(true);
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) { console.warn("create: clear storage skipped", e); }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setServerError(typeof detail === "string" ? detail : "Une erreur est survenue, merci de réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-[var(--mc-navy-950)] min-h-screen">
        <SEO title="Messagerie créée · MetaConnect" canonical="/mssante/espace" noIndex />
        <section className="hex-grid pt-32 pb-20 lg:pt-40">
          <div className="max-w-xl mx-auto px-5 lg:px-8 text-center">
            <div className="mx-auto grid place-items-center h-16 w-16 rounded-full bg-[rgba(110,231,197,0.14)] border border-[var(--mc-mint)] text-emerald-600 mb-6">
              <Check size={28} />
            </div>
            <h1 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl font-semibold leading-tight">
              Votre messagerie est en cours de création
            </h1>
            {doneEmail && (
              <div className="mt-5 inline-flex items-center gap-2 text-[13px] rounded-full px-4 py-2 bg-[rgba(110,231,197,0.08)] border border-[rgba(110,231,197,0.3)] text-emerald-600">
                <Mail size={13} /> {doneEmail}
              </div>
            )}
            <ul className="mt-7 grid gap-3 text-left max-w-md mx-auto">
              <Li>Email de confirmation envoyé à {selectedUser?.email || user?.email}.</Li>
              <Li>Vous pouvez maintenant <strong>ajouter d'autres utilisateurs</strong> via « Gestion des accès ».</Li>
              <Li>Vos identifiants de connexion vous parviendront sous <strong>24 à 48h ouvrées</strong>.</Li>
            </ul>
            <div className="mt-9 flex items-center justify-center gap-3 flex-wrap">
              <Link to="/mssante/espace" data-testid="cm-back-espace"
                    className="btn-primary px-6 py-3 rounded-full font-medium inline-flex items-center gap-2">
                Retour à mon espace <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="bg-[var(--mc-navy-950)] min-h-screen" data-testid="page-mssante-create">
      <SEO title="Nouvelle messagerie MSSanté · MetaConnect"
           canonical="/mssante/espace/messagerie/nouvelle" schema={SCHEMA} noIndex />

      <section className="hex-grid pt-32 pb-10">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <Link to="/mssante/espace" className="text-[12px] text-[#475569] hover:text-[#0F2B5B] inline-flex items-center gap-1.5">
            <ArrowLeft size={12} /> Retour à mon espace
          </Link>
          <div className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-emerald-600">
            <ShieldCheck size={13} /> Création MSSanté
          </div>
          <h1 className="mt-3 font-display text-[#0F2B5B] text-3xl sm:text-4xl font-semibold leading-tight">
            Nouvelle messagerie sécurisée
          </h1>
          <p className="mt-2 text-[13px] text-[#475569]">
            Création rapide en 3 étapes. Désignez votre responsable, et — si besoin — d'autres cotitulaires/délégataires dès cette étape.
          </p>
          {user && user.email_verified === false && (
            <div className="mt-6">
              <VerifyEmailBanner email={user.email} onVerified={refresh} />
            </div>
          )}
        </div>
      </section>

      {user && user.email_verified === false ? null : (
      <section className="hex-soft py-10 lg:py-12">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <Stepper step={step} />
          <div className="mc-card mt-7 p-6 lg:p-8">
            {step === 1 && (
              <StepStructure form={form} set={set} errors={errors}
                             structures={structures} loading={loadingStructures}
                             onCreated={(s) => { setStructures((arr) => [s, ...arr]); set("structure_ref_id", s.id); }} />
            )}
            {step === 2 && (
              <StepResponsible form={form} set={set} errors={errors}
                               structureId={form.structure_ref_id} users={users}
                               onCreated={(u) => { setUsers((arr) => [...arr, u]); set("responsible_user_id", u.id); }} />
            )}
            {step === 3 && (
              <>
                <StepMailbox form={form} set={set} errors={errors} fullEmail={fullEmail}
                             structure={selectedStructure} responsible={selectedUser}
                             users={users} />
                <SubscriptionStep form={form} set={set} />
              </>
            )}

            {serverError && (
              <div className="mt-5 flex items-start gap-2 rounded-lg border border-[rgba(255,120,120,0.35)] bg-[rgba(255,120,120,0.08)] px-4 py-3 text-[13px] text-[#FFB4B4]">
                <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{serverError}</span>
              </div>
            )}

            <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
              {step > 1 ? (
                <button onClick={prev} data-testid="cm-prev"
                        className="btn-ghost px-5 py-2.5 rounded-full text-[13px] font-medium inline-flex items-center gap-1.5">
                  <ArrowLeft size={14} /> Précédent
                </button>
              ) : <span />}
              {step < 3 ? (
                <button onClick={next} data-testid="cm-next"
                        className="btn-primary px-6 py-3 rounded-full text-[13px] font-medium inline-flex items-center gap-1.5">
                  Continuer <ArrowRight size={14} />
                </button>
              ) : (
                <button onClick={submit} disabled={submitting || !form.contract_accepted} data-testid="cm-submit"
                        className="btn-primary px-6 py-3 rounded-full text-[13px] font-medium inline-flex items-center gap-1.5 disabled:opacity-60">
                  {submitting && <Loader2 size={14} className="animate-spin" />}
                  {submitting ? "Redirection vers le paiement..." : "Souscrire et payer"}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
      )}
    </div>
  );
}

// =================== Atoms / Sub-components ===================

function Li({ children }) {
  return (
    <li className="flex items-start gap-2.5 text-[14px] text-[#475569]">
      <Check size={16} className="text-emerald-600 mt-0.5 shrink-0" /><span>{children}</span>
    </li>
  );
}

function Stepper({ step }) {
  const steps = [
    { id: 1, label: "Structure", Icon: Building2 },
    { id: 2, label: "Responsable", Icon: User },
    { id: 3, label: "Messagerie", Icon: MailPlus },
  ];
  return (
    <div className="flex items-center justify-between gap-2" data-testid="cm-stepper">
      {steps.map((s, i) => {
        const done = step > s.id, active = step === s.id, Icon = s.Icon;
        return (
          <React.Fragment key={s.id}>
            <div className="flex items-center gap-2.5 min-w-0">
              <div className={`grid place-items-center h-9 w-9 rounded-full border transition ${
                done ? "bg-[rgba(110,231,197,0.14)] border-[var(--mc-mint)] text-emerald-600"
                : active ? "bg-[rgba(45,123,255,0.18)] border-[var(--mc-blue-400)] text-[#0F2B5B]"
                : "bg-transparent border-slate-200 text-[#475569]"
              }`}>
                {done ? <Check size={15} /> : <Icon size={15} />}
              </div>
              <div className={`text-[12px] font-medium ${active || done ? "text-[#0F2B5B]" : "text-[#475569]"}`}>
                <span className="block text-[10px] uppercase tracking-[0.18em] opacity-70">Étape {s.id}</span>
                <span>{s.label}</span>
              </div>
            </div>
            {i < steps.length - 1 && <div className={`flex-1 h-px ${step > s.id ? "bg-[var(--mc-mint)]" : "bg-[rgba(127,179,255,0.18)]"}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function StepStructure({ form, set, errors, structures, loading, onCreated }) {
  const [addOpen, setAddOpen] = useState(false);
  return (
    <div className="grid gap-4">
      <Title icon={<Building2 size={16} />}>Choisissez une structure</Title>
      <p className="text-[12.5px] text-[#475569] -mt-2">
        Sélectionnez la structure (entité juridique) à laquelle cette messagerie sera rattachée.
      </p>

      {loading ? (
        <div className="text-[#475569] text-[13px]">Chargement de vos structures…</div>
      ) : structures.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 p-5 text-center text-[12.5px] text-[#475569]">
          Aucune structure pour le moment. Créez-en une pour démarrer.
        </div>
      ) : (
        <div className="grid gap-2" data-testid="cm-structures-list">
          {structures.map((s) => (
            <label key={s.id}
                   className={`flex items-center gap-3 rounded-lg border p-3 lg:p-4 cursor-pointer transition ${
                     form.structure_ref_id === s.id
                       ? "border-[var(--mc-blue-400)] bg-[rgba(45,123,255,0.08)]"
                       : "border-[rgba(127,179,255,0.16)] hover:border-[rgba(127,179,255,0.32)]"
                   }`}>
              <input type="radio" name="structure" className="sr-only"
                     checked={form.structure_ref_id === s.id}
                     onChange={() => set("structure_ref_id", s.id)}
                     data-testid={`cm-structure-${s.id}`} />
              <div className={`grid place-items-center h-9 w-9 rounded-full ${
                form.structure_ref_id === s.id ? "bg-[var(--mc-blue-500)] text-[#0F2B5B]" : "bg-[#EFF6FF] text-[#0369A1]"
              }`}>
                <Building2 size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[#0F2B5B] font-medium truncate">{s.structure_name}</div>
                <div className="text-[11.5px] text-[#475569] mt-0.5">
                  {s.structure_id_type} <span className="font-mono">{s.structure_id}</span>
                  <span className="mx-2">·</span>{s.user_count || 0} utilisateur(s)
                  <span className="mx-2">·</span>{s.mailbox_count || 0} messagerie(s)
                </div>
              </div>
              {form.structure_ref_id === s.id && <Check size={16} className="text-[#0369A1]" />}
            </label>
          ))}
        </div>
      )}
      {errors.structure_ref_id && <Err>{errors.structure_ref_id}</Err>}

      {!addOpen ? (
        <button type="button" onClick={() => setAddOpen(true)} data-testid="cm-add-structure"
                className="btn-ghost mt-2 px-4 py-2 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5 self-start">
          <Plus size={13} /> Ajouter une structure
        </button>
      ) : (
        <NewStructureForm onCancel={() => setAddOpen(false)} onCreated={(s) => { onCreated(s); setAddOpen(false); }} />
      )}
    </div>
  );
}

function NewStructureForm({ onCancel, onCreated }) {
  return <StructureAnnuaireForm onCancel={onCancel} onCreated={onCreated} testid="cm-annu" />;
}

function StepResponsible({ form, set, errors, structureId, users, onCreated }) {
  const [addOpen, setAddOpen] = useState(false);

  const setRole = (uid, role) => {
    // role: 'none' | 'cotitulaire' | 'delegataire'
    const co = new Set(form.cotitulaire_user_ids || []);
    const dl = new Set(form.delegataire_user_ids || []);
    co.delete(uid); dl.delete(uid);
    if (role === "cotitulaire") co.add(uid);
    else if (role === "delegataire") dl.add(uid);
    set("cotitulaire_user_ids", Array.from(co));
    set("delegataire_user_ids", Array.from(dl));
  };
  const roleOf = (uid) => {
    if ((form.cotitulaire_user_ids || []).includes(uid)) return "cotitulaire";
    if ((form.delegataire_user_ids || []).includes(uid)) return "delegataire";
    return "none";
  };

  const otherUsers = users.filter((u) => u.id !== form.responsible_user_id);

  return (
    <div className="grid gap-4">
      <Title icon={<User size={16} />}>Responsable de la messagerie</Title>
      <p className="text-[12.5px] text-[#475569] -mt-2">
        Le responsable a la pleine responsabilité de la BAL. Vous pourrez ensuite désigner d'éventuels cotitulaires et délégataires.
      </p>

      <div className="rounded-lg border border-emerald-200 bg-[rgba(110,231,197,0.06)] p-3.5 flex items-start gap-2.5" data-testid="cm-help-responsable">
        <Info size={14} className="text-emerald-600 mt-0.5 shrink-0" />
        <p className="text-[12.5px] text-[#334155] leading-relaxed">
          <strong className="text-emerald-600">Comment procéder :</strong> une fois tous les
          utilisateurs créés, <strong>cliquez sur le nom du responsable</strong>, puis
          <strong> attribuez un rôle</strong> (cotitulaire ou délégataire) <strong>à chacun des
          autres utilisateurs</strong> avant de continuer.
        </p>
      </div>

      {users.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 p-5 text-center text-[12.5px] text-[#475569]">
          Aucun utilisateur dans cette structure. Ajoutez-en un pour continuer.
        </div>
      ) : (
        <div className="grid gap-2" data-testid="cm-users-list">
          {users.map((u) => {
            const selected = form.responsible_user_id === u.id;
            return (
              <button
                key={u.id}
                type="button"
                onClick={() => set("responsible_user_id", u.id)}
                data-testid={`cm-user-${u.id}`}
                className={`w-full text-left flex items-center gap-3 rounded-lg border p-3 lg:p-4 cursor-pointer transition ${
                  selected
                    ? "border-[var(--mc-blue-400)] bg-[rgba(45,123,255,0.08)] ring-1 ring-[var(--mc-blue-400)]"
                    : "border-[rgba(127,179,255,0.16)] hover:border-[rgba(127,179,255,0.32)]"
                }`}
              >
                <div className={`grid place-items-center h-9 w-9 rounded-full shrink-0 ${
                  selected ? "bg-[var(--mc-blue-500)] text-[#0F2B5B]" : "bg-[#EFF6FF] text-[#0369A1]"
                }`}>
                  <User size={13} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[#0F2B5B] font-medium truncate">{u.first_name} {u.last_name}</div>
                  <div className="text-[11.5px] text-[#475569] mt-0.5">
                    {u.profession}
                    {u.id_type && u.professional_id && <> · {getIdTypeMeta(u.id_type)?.label || u.id_type} <span className="font-mono">{u.professional_id}</span></>}
                    <span className="mx-2">·</span>{u.email}
                  </div>
                </div>
                {selected && (
                  <span className="text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border border-[rgba(110,231,197,0.4)] text-emerald-600 shrink-0">
                    Responsable
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
      {errors.responsible_user_id && <Err>{errors.responsible_user_id}</Err>}

      {!addOpen ? (
        <button type="button" onClick={() => setAddOpen(true)} data-testid="cm-add-user"
                className="btn-ghost mt-2 px-4 py-2 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5 self-start">
          <Plus size={13} /> Ajouter un utilisateur
        </button>
      ) : (
        <NewStructureUserForm structureId={structureId}
                              onCancel={() => setAddOpen(false)}
                              onCreated={(u) => { onCreated(u); setAddOpen(false); }} />
      )}

      {/* Cotitulaires / Délégataires (optional) */}
      {form.responsible_user_id && otherUsers.length > 0 && (
        <div className="mt-3 rounded-lg border border-[rgba(127,179,255,0.16)] bg-[#F8FAFC] p-4 lg:p-5"
             data-testid="cm-roles-block">
          <div className="text-[12px] uppercase tracking-[0.16em] text-[#0369A1] inline-flex items-center gap-2 mb-1">
            <Users size={12} /> Autres rôles (optionnel)
          </div>
          <p className="text-[12px] text-[#475569] mb-3">
            Désignez d'autres utilisateurs comme <strong className="text-[#0F2B5B]">cotitulaires</strong> (pleine utilisation)
            ou <strong className="text-[#0F2B5B]">délégataires</strong> (lecture / délégation). Vous pourrez modifier ces accès à tout moment.
          </p>
          <div className="grid gap-2">
            {otherUsers.map((u) => {
              const role = roleOf(u.id);
              return (
                <div key={u.id}
                     className="flex items-center gap-3 rounded-lg border border-slate-100 bg-white p-3"
                     data-testid={`cm-role-row-${u.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-[#0F2B5B] text-[13px] font-medium truncate">{u.first_name} {u.last_name}</div>
                    <div className="text-[11px] text-[#475569] mt-0.5 truncate">
                      {u.profession} · {u.email}
                    </div>
                  </div>
                  <div className="inline-flex p-1 rounded-full bg-[rgba(127,179,255,0.08)] border border-slate-200 shrink-0">
                    {[
                      { v: "none", l: "Aucun" },
                      { v: "cotitulaire", l: "Cotitulaire" },
                      { v: "delegataire", l: "Délégataire" },
                    ].map((opt) => (
                      <button key={opt.v} type="button"
                              onClick={() => setRole(u.id, opt.v)}
                              data-testid={`cm-role-${u.id}-${opt.v}`}
                              className={`px-3 py-1 rounded-full text-[11.5px] font-medium transition ${
                                role === opt.v
                                  ? (opt.v === "cotitulaire"
                                      ? "bg-[var(--mc-blue-500)] text-[#0F2B5B]"
                                      : opt.v === "delegataire"
                                        ? "bg-[#7c5cff] text-[#0F2B5B]"
                                        : "bg-[rgba(127,179,255,0.18)] text-[#0F2B5B]")
                                  : "text-[#475569] hover:text-[#0F2B5B]"
                              }`}>
                        {opt.l}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function NewStructureUserForm({ structureId, onCancel, onCreated }) {
  const [t, setT] = useState({
    last_name: "", first_name: "", email: "", phone: "",
    profession: "", id_type: "RPPS", professional_id: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [profQuery, setProfQuery] = useState("");
  const [profOpen, setProfOpen] = useState(false);

  const validation = validateUserId(t.id_type, t.professional_id);
  const idError = submitAttempted && !validation.ok ? validation.error : null;

  const profFiltered = profQuery
    ? PROFESSIONS.filter((p) => p.toLowerCase().includes(profQuery.toLowerCase()))
    : PROFESSIONS;

  const submit = async () => {
    setSubmitAttempted(true);
    setErr("");
    if (!validation.ok) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/pro/structures/${structureId}/users`, t);
      onCreated(data);
    } catch (e) {
      setErr(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Création impossible");
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-slate-200 p-4 lg:p-5 bg-[#F8FAFC] grid gap-3"
         data-testid="cm-new-user-form">
      <div className="text-[12px] uppercase tracking-[0.16em] text-[#0369A1]">Nouvel utilisateur</div>
<ProsAnnuaireSuggestions structureId={structureId} onChoisir={(p) => { setProfQuery(""); setT((s) => ({ ...s, last_name: p.nom, first_name: p.prenom, profession: p.profession || s.profession, id_type: "RPPS", professional_id: p.rpps })); }} />
      <div className="grid md:grid-cols-2 gap-3">
        <Input label="Nom *" value={t.last_name} onChange={(v) => setT({ ...t, last_name: v })} testid="cm-new-user-last" />
        <Input label="Prénom *" value={t.first_name} onChange={(v) => setT({ ...t, first_name: v })} testid="cm-new-user-first" />
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Input type="email" label="Email *" value={t.email} onChange={(v) => setT({ ...t, email: v })} testid="cm-new-user-email" />
        <Input label="Téléphone" value={t.phone} onChange={(v) => setT({ ...t, phone: v })} testid="cm-new-user-phone" />
      </div>
      <div className="relative">
        <Lbl>Profession *</Lbl>
        <div className="relative">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#475569]" />
          <input
            data-testid="cm-new-user-profession"
            className="mc-input w-full pl-11 pr-10 cursor-pointer"
            value={profQuery || t.profession}
            onChange={(e) => { setProfQuery(e.target.value); setT((s) => ({ ...s, profession: "" })); setProfOpen(true); }}
            onFocus={() => setProfOpen(true)}
            onClick={() => setProfOpen(true)}
            onBlur={() => setTimeout(() => setProfOpen(false), 150)}
            placeholder="Cliquez pour choisir ou saisissez pour rechercher..."
            autoComplete="off"
          />
          <ChevronDown
            size={16}
            onMouseDown={(e) => { e.preventDefault(); setProfOpen((o) => !o); }}
            className={`absolute right-3.5 top-1/2 -translate-y-1/2 text-[#475569] cursor-pointer transition-transform ${profOpen ? "rotate-180" : ""}`}
          />
        </div>
        {profOpen && profFiltered.length > 0 && (
          <ul
            className="absolute z-20 mt-1 left-0 right-0 max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-[var(--mc-navy-900)] shadow-lg"
            data-testid="cm-new-user-profession-list"
          >
            {profFiltered.slice(0, 60).map((p) => (
              <li
                key={p}
                onMouseDown={(e) => { e.preventDefault(); setT((s) => ({ ...s, profession: p })); setProfQuery(p); setProfOpen(false); }}
                className="px-4 py-2 text-[13px] text-[#475569] hover:bg-[rgba(45,123,255,0.16)] cursor-pointer"
              >
                {p}
              </li>
            ))}
          </ul>
        )}
      </div>
      <UserIdField idType={t.id_type} value={t.professional_id}
                   onChange={({ id_type, professional_id }) => setT({ ...t, id_type, professional_id })}
                   errorOverride={idError} idPrefix="cm-userid"
 onPraticien={(d) => setT((s) => ({ ...s, last_name: s.last_name || d.nom || "", first_name: s.first_name || prenomPropre(d.prenom), profession: s.profession || professionConnue(d.profession) }))} />
      {err && <Err>{err}</Err>}
      <div className="flex items-center gap-2 justify-end">
        <button onClick={onCancel} className="btn-ghost px-4 py-2 rounded-full text-[12px]">Annuler</button>
        <button onClick={submit}
                disabled={busy || !t.last_name || !t.first_name || !t.email || !t.profession || !t.id_type || !t.professional_id}
                className="btn-primary px-4 py-2 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="cm-new-user-submit">
          {busy && <Loader2 size={12} className="animate-spin" />}Créer
        </button>
      </div>
    </div>
  );
}

function StepMailbox({ form, set, errors, fullEmail, structure, responsible, users }) {
  const co = (form.cotitulaire_user_ids || []).map((id) => users.find((u) => u.id === id)).filter(Boolean);
  const dl = (form.delegataire_user_ids || []).map((id) => users.find((u) => u.id === id)).filter(Boolean);
  return (
    <div className="grid gap-4">
      <Title icon={<MailPlus size={16} />}>Configuration de la messagerie</Title>
      <div className="rounded-lg border border-[rgba(127,179,255,0.16)] bg-[#F8FAFC] p-3 text-[12.5px] text-[#475569] grid gap-1">
        <div><span className="text-[#475569]">Structure :</span> {structure?.structure_name} <span className="font-mono">({structure?.structure_id_type} {structure?.structure_id})</span></div>
        <div><span className="text-[#475569]">Responsable :</span> {responsible?.first_name} {responsible?.last_name} — {responsible?.email}</div>
        {co.length > 0 && (
          <div><span className="text-[#475569]">Cotitulaire(s) :</span> {co.map((u) => `${u.first_name} ${u.last_name}`).join(", ")}</div>
        )}
        {dl.length > 0 && (
          <div><span className="text-[#475569]">Délégataire(s) :</span> {dl.map((u) => `${u.first_name} ${u.last_name}`).join(", ")}</div>
        )}
      </div>
      <div>
        <Lbl>Nom de la boîte * (minuscules, chiffres, tirets)</Lbl>
        <div className="flex items-stretch">
          <input data-testid="cm-mailbox-name" className="mc-input rounded-r-none flex-1"
                 value={form.mailbox_name}
                 onChange={(e) => set("mailbox_name", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                 placeholder="cabinet-lefevre" maxLength={64} />
          <select data-testid="cm-mailbox-domain"
                  className="mc-input rounded-l-none border-l-0 max-w-[280px]"
                  value={form.mailbox_domain} onChange={(e) => set("mailbox_domain", e.target.value)}>
            {MSSANTE_DOMAINS.map((d) => <option key={d} value={d}>@{d}</option>)}
          </select>
        </div>
        {errors.mailbox_name && <Err>{errors.mailbox_name}</Err>}
        {fullEmail && (
          <div className="mt-2 inline-flex items-center gap-2 text-[12px] rounded-full px-3 py-1.5 bg-[rgba(110,231,197,0.08)] border border-[rgba(110,231,197,0.25)] text-emerald-600">
            <Mail size={12} /> {fullEmail}
          </div>
        )}
      </div>
      <Input label="Description *" value={form.mailbox_description}
             onChange={(v) => set("mailbox_description", v)}
             placeholder="Boîte principale du cabinet, échanges patients et confrères"
             error={errors.mailbox_description} testid="cm-desc" />
      <div className="mc-card p-4 flex items-start justify-between gap-3 bg-[rgba(255,176,110,0.05)] border-[rgba(255,176,110,0.25)]">
        <div className="text-[12.5px]">
          <div className="text-[#0F2B5B] font-medium flex items-center gap-2">
            <AlertTriangle size={14} className="text-[#ffb06e]" /> Liste rouge
          </div>
          <p className="text-[#475569] mt-0.5">Ne pas faire apparaître l'adresse dans l'annuaire MSSanté public.</p>
          <p className="text-[#ffb06e] mt-2 text-[12px] leading-relaxed" data-testid="cm-redlist-warning">
            Attention : cocher cette case masquera votre BAL de l'annuaire public MSSanté — vos confrères ne pourront plus la trouver pour vous écrire. Nous recommandons de <strong>ne pas</strong> la cocher.
          </p>
        </div>
        <label className="inline-flex items-center cursor-pointer shrink-0">
          <input type="checkbox" className="sr-only peer" data-testid="cm-redlist"
                 checked={form.mailbox_red_list}
                 onChange={(e) => set("mailbox_red_list", e.target.checked)} />
          <span className="relative w-11 h-6 bg-[rgba(127,179,255,0.18)] rounded-full peer-checked:bg-[#ffb06e] transition before:absolute before:top-0.5 before:left-0.5 before:h-5 before:w-5 before:bg-white before:rounded-full before:transition peer-checked:before:translate-x-5" />
        </label>
      </div>
      <Input label="Référence de facturation (optionnel)"
             value={form.billing_ref} onChange={(v) => set("billing_ref", v)}
             placeholder="Bon de commande, référence interne..." testid="cm-ref" />
      {/* Honeypot */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
        <input type="text" tabIndex={-1} autoComplete="off"
               value={form.website} onChange={(e) => set("website", e.target.value)} />
      </div>
    </div>
  );
}

// Atoms
function Title({ icon, children }) {
  return <div className="flex items-center gap-2 mb-1 text-[#0F2B5B]"><span className="text-[#0369A1]">{icon}</span><h2 className="font-display text-[18px]">{children}</h2></div>;
}
function Lbl({ children }) { return <span className="block text-[11px] uppercase tracking-[0.16em] text-[#0369A1] mb-2">{children}</span>; }
function Err({ children }) { return <p className="mt-1.5 text-[12px] text-[#FFB4B4] inline-flex items-center gap-1"><AlertCircle size={12} /> {children}</p>; }
function Input({ label, value, onChange, placeholder, error, type = "text", inputMode, testid }) {
  return (
    <div>
      <Lbl>{label}</Lbl>
      <input type={type} inputMode={inputMode} data-testid={testid}
             className="mc-input w-full" value={value || ""}
             onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      {error && <Err>{error}</Err>}
    </div>
  );
}
/* ----- Subscription step (plan selector + contract acceptance) ----- */
function SubscriptionStep({ form, set }) {
  return (
    <div className="mt-10 pt-8 border-t border-[rgba(127,179,255,0.14)]" data-testid="subscription-step">
      <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em] text-emerald-600">
        <span className="h-px w-7 bg-[var(--mc-mint)]" />
        Dernière action — Souscription
      </div>
      <h3 className="mt-3 font-display text-[#0F2B5B] text-xl lg:text-2xl">
        Choisissez votre formule
      </h3>
      <p className="mt-2 text-[13px] text-[#475569]">
        Engagement initial 12 mois — reconduction tacite annuelle — préavis de résiliation 3 mois.
        Paiement par prélèvement automatique SEPA. TVA&nbsp;20&nbsp;% en sus.
      </p>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <PlanCard
          active={form.billing_plan === "monthly"}
          onSelect={() => set("billing_plan", "monthly")}
          label="Mensuel"
          price="19,99 € HT"
          unit="par mois / BAL"
          subtitle="23,99 € TTC (TVA 20% incluse) — souplesse mensuelle"
          testid="plan-monthly"
        />
        <PlanCard
          active={form.billing_plan === "yearly"}
          onSelect={() => set("billing_plan", "yearly")}
          label="Annuel"
          price="199,99 € HT"
          unit="par an / BAL"
          subtitle="239,99 € TTC (TVA 20% incluse) — 2 mois offerts"
          highlight
          testid="plan-yearly"
        />
      </div>

      <label className="mt-7 flex items-start gap-3 rounded-lg border border-[rgba(127,179,255,0.20)] bg-[#F8FAFC] p-4 cursor-pointer">
        <input
          type="checkbox"
          checked={!!form.contract_accepted}
          onChange={(e) => set("contract_accepted", e.target.checked)}
          data-testid="contract-accept-checkbox"
          className="mt-0.5 h-4 w-4 accent-[var(--mc-blue-400)] shrink-0"
        />
        <span className="text-[13px] text-[#334155] leading-relaxed">
          J'ai lu et j'accepte le{" "}
          <a href="/contrat" target="_blank" rel="noreferrer"
             data-testid="contract-link"
             className="text-[#0369A1] underline underline-offset-2 hover:text-[#0F2B5B]">
            contrat MSSanté MetaConnect (v1)
          </a>
          {" "}— SASU au capital de 1 000 €, SIRET 931 999 833 00011. La validation lance la signature
          du mandat SEPA et la mise en service de la BAL après confirmation du prélèvement.
        </span>
      </label>
    </div>
  );
}

function PlanCard({ active, onSelect, label, price, unit, subtitle, highlight, testid }) {
  return (
    <button type="button" onClick={onSelect} data-testid={testid}
            className={`text-left rounded-xl p-5 lg:p-6 transition border ${
              active
                ? "border-[var(--mc-mint)] bg-[rgba(110,231,197,0.08)]"
                : "border-slate-200 bg-white hover:border-[rgba(127,179,255,0.35)]"
            }`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.20em] text-[#475569]">
          {label}
        </span>
        {highlight && (
          <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border border-emerald-200 bg-[rgba(110,231,197,0.08)] text-emerald-600">
            Recommandé
          </span>
        )}
      </div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className="font-display text-[#0F2B5B] text-2xl lg:text-3xl">{price}</span>
        <span className="text-[12px] text-[#475569]">{unit}</span>
      </div>
      <p className="mt-2 text-[12.5px] text-[#475569]">{subtitle}</p>
      <div className={`mt-4 inline-flex items-center gap-1.5 text-[11.5px] ${active ? "text-emerald-600" : "text-[#475569]"}`}>
        <span className={`h-3.5 w-3.5 rounded-full border ${active ? "bg-[var(--mc-mint)] border-[var(--mc-mint)]" : "border-[var(--mc-muted)]"}`} />
        {active ? "Sélectionné" : "Choisir"}
      </div>
    </button>
  );
}
